import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
  useRef,
} from "react";
import { Sessionable } from "../core/sessionable";
import {
  SessionRecorderProps,
  SessionMetrics,
  UseSessionableResult,
  UseSessionableMetricsResult,
} from "../types";

// Split the context into two separate contexts to prevent unnecessary re-renders
// One for the instance (rarely changes) and one for metrics (changes frequently)
interface SessionableInstanceContextType {
  instance: Sessionable | null;
}

interface SessionableMetricsContextType {
  metrics: SessionMetrics | null;
}

const SessionableInstanceContext = createContext<SessionableInstanceContextType>({
  instance: null,
});

const SessionableMetricsContext = createContext<SessionableMetricsContextType>({
  metrics: null,
});

// Component that provides metrics context and handles updates
// This is key to preventing re-render cascades
const MetricsProvider: React.FC<{
  instance: Sessionable | null;
  children: React.ReactNode;
  debug?: boolean;
}> = React.memo(({ instance, children, debug }) => {
  const [metrics, setMetrics] = useState<SessionMetrics | null>(null);
  const metricsRef = useRef<SessionMetrics | null>(null);
  
  // Setup metrics polling
  useEffect(() => {
    if (!instance) return;
    
    // Initial metrics
    const initialMetrics = instance.getMetrics();
    metricsRef.current = initialMetrics;
    setMetrics(initialMetrics);
    
    if (debug) {
      console.log('Metrics provider initialized', {
        time: new Date().toISOString(),
      });
    }
    
    // Update metrics periodically - but only if they've actually changed
    const intervalId = setInterval(() => {
      if (!instance) return;
      
      const newMetrics = instance.getMetrics();
      
      // Do a deep comparison to avoid unnecessary state updates
      const currentMetricsStr = JSON.stringify(metricsRef.current);
      const newMetricsStr = JSON.stringify(newMetrics);
      const isChanged = currentMetricsStr !== newMetricsStr;
      
      if (isChanged) {
        if (debug) {
          console.log('Metrics updated', {
            time: new Date().toISOString(),
            eventsRecorded: newMetrics.eventsRecorded,
            connectionStatus: newMetrics.connectionStatus
          });
        }
        
        // Update both state and ref
        metricsRef.current = newMetrics;
        setMetrics(newMetrics);
      }
    }, 1000);
    
    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [instance, debug]);
  
  // Create metrics context value
  const metricsContextValue = useMemo(() => ({ metrics }), [metrics]);
  
  if (debug) {
    console.log('MetricsProvider rendered', new Date().toISOString());
  }
  
  return (
    <SessionableMetricsContext.Provider value={metricsContextValue}>
      {children}
    </SessionableMetricsContext.Provider>
  );
});

/**
 * Hook to access only the metrics from the Sessionable instance.
 * Use this when you only need the metrics and want to avoid re-renders
 * when other parts of the Sessionable instance change.
 * @returns {UseSessionableMetricsResult} The current metrics or null if not available
 */
export const useSessionableMetrics = (): UseSessionableMetricsResult => {
  const metricsContext = useContext(SessionableMetricsContext);
  
  if (!metricsContext) {
    throw new Error("useSessionableMetrics must be used within a SessionableProvider");
  }
  
  return metricsContext.metrics;
};

/**
 * Custom hook to access the Sessionable instance and the stable isRecording state.
 * This hook only subscribes to the instance context and won't re-render when metrics change.
 * 
 * @returns {UseSessionableResult} Object containing the Sessionable instance and convenience methods
 */
export const useSessionable = (): UseSessionableResult => {
  const instanceContext = useContext(SessionableInstanceContext);
  const [isRecording, setIsRecording] = useState(false);

  if (!instanceContext) {
    throw new Error("useSessionable must be used within a SessionableProvider");
  }

  const instance = instanceContext.instance;

  // Update isRecording from instance, but not on every metrics change
  useEffect(() => {
    if (!instance) return;
    
    // Initial state
    setIsRecording(instance.isCurrentlyRecording());
    
    // Listen for recording state changes only
    const handleRecordingStateChange = (event: CustomEvent) => {
      if (event.detail && typeof event.detail.isRecording === 'boolean') {
        setIsRecording(event.detail.isRecording);
      }
    };
    
    // Add event listener for recording state changes
    window.addEventListener(
      "sessionable:recording_state_change",
      handleRecordingStateChange as EventListener
    );
    
    return () => {
      window.removeEventListener(
        "sessionable:recording_state_change",
        handleRecordingStateChange as EventListener
      );
    };
  }, [instance]);

  // Use useMemo to ensure we only create a new object when dependencies change
  // This is crucial to prevent unnecessary re-renders in components using this hook
  return useMemo(() => ({
    sessionable: instance,
    isRecording,
    // Convenience methods
    start: () => {
      if (instance) {
        instance.start();
      }
    },
    stop: (clearSession?: boolean) => {
      if (instance) {
        instance.stop(clearSession);
      }
    },
    addMetadata: (data: Record<string, any>) => {
      if (instance) {
        instance.addMetadata(data);
      }
    },
    identify: async (
      userId: string,
      organizationId?: string,
      userAttributes?: Record<string, any>
    ) => {
      if (instance) {
        if (!isRecording) {
          throw new Error(
            "Cannot identify a user when session is not recording. " +
              "Check isRecording before calling identify()."
          );
        }
        return instance.identify(
          userId,
          organizationId,
          userAttributes
        );
      }
    },
    getUserId: () => {
      if (instance) {
        return instance.getUserId();
      }
      return null;
    },
    getOrganizationId: () => {
      if (instance) {
        return instance.getOrganizationId();
      }
      return null;
    },
    getUserAttributes: () => {
      if (instance) {
        return instance.getUserAttributes();
      }
      return null;
    },
  }), [instance, isRecording]);
};

/**
 * Inner component that provides the Sessionable instance
 * Completely separated from metrics updates
 */
const SessionableInstanceProvider = React.memo(({
  sessionable,
  children,
  debug
}: {
  sessionable: Sessionable | null;
  children: React.ReactNode;
  debug?: boolean;
}) => {
  // Create instance context value - this rarely changes
  const instanceContextValue = useMemo(
    () => ({ instance: sessionable }),
    [sessionable]
  );
  
  if (debug) {
    console.log('SessionableInstanceProvider rendered', new Date().toISOString());
  }
  
  return (
    <SessionableInstanceContext.Provider value={instanceContextValue}>
      {children}
    </SessionableInstanceContext.Provider>
  );
});

/**
 * React component for Sessionable recording
 * This is a provider component that makes the Sessionable instance
 * available to child components via the useSessionable hook.
 */
export const SessionRecorder: React.FC<
  React.PropsWithChildren<SessionRecorderProps>
> = (props) => {
  const {
    children,
    apiKey,
    autoStart = false,
    metadata = {},
    maskInputs = false,
    maskTextSelector,
    debug = false,
    useDeviceId = true,
    recordLocalhost = false,
    userId,
    organizationId,
    userAttributes,
    ...otherProps
  } = props;

  // Use state for the sessionable instance
  const [sessionable, setSessionable] = useState<Sessionable | null>(null);
  
  // Initialize Sessionable on mount - only once per apiKey
  useEffect(() => {
    // Create the Sessionable instance with all props automatically passed
    const instance = new Sessionable({
      apiKey,
      autoStart,
      metadata,
      maskInputs,
      maskTextSelector,
      debug,
      useDeviceId,
      recordLocalhost,
      userId,
      organizationId,
      userAttributes,
      ...otherProps,
    });
    
    // Store in state to trigger a single re-render
    setSessionable(instance);
    
    if (debug) {
      console.log('Sessionable instance created', {
        time: new Date().toISOString(),
        apiKey: apiKey.substring(0, 5) + '...',
      });
    }

    // Cleanup on unmount
    return () => {
      if (instance) {
        instance.stop();
      }
    };
  }, [
    // Only depend on apiKey to prevent re-initialization
    apiKey,
    autoStart,
    debug,
    maskInputs,
    maskTextSelector,
    metadata,
    organizationId,
    recordLocalhost,
    useDeviceId,
    userId,
    userAttributes,
  ]);
  
  if (debug) {
    console.log('SessionRecorder rendered', new Date().toISOString());
  }

  // Nest the providers to make both contexts available while isolating render cycles
  // The outer provider (metrics) can update without causing the inner tree to re-render
  return (
    <MetricsProvider 
      instance={sessionable}
      debug={debug}
    >
      {/* Memoized instance provider prevents re-renders from metrics updates */}
      <SessionableInstanceProvider 
        sessionable={sessionable}
        debug={debug}
      >
        {children}
      </SessionableInstanceProvider>
    </MetricsProvider>
  );
};
